import { clearSession, getSession, isExpiring, setSession } from './session.js';

/**
 * The single place that talks to the backend.
 *
 * Empty by default: the dev server proxies /api and /health, so the SPA is same-origin in development
 * and in production. VITE_API_BASE overrides that for a split deployment.
 */
const BASE = import.meta.env?.VITE_API_BASE ?? '';

/** Thrown for any non-2xx response, carrying enough for a view to render a useful message. */
export class ApiError extends Error
{
   constructor(status, message, details)
   {
      super(message);
      this.name = 'ApiError';
      this.status = status;
      this.details = details;
   }
}

/**
 * Serialised so a burst of parallel requests hitting an expired token produces one refresh, not one
 * per request - each of which would rotate the refresh token out from under the others.
 */
let refreshInFlight = null;

function url(path)
{
   return `${BASE}${path}`;
}

async function parse(response)
{
   if (response.status === 204 || response.headers.get('content-length') === '0')
   {
      return null;
   }

   const type = response.headers.get('content-type') ?? '';
   return type.includes('json') ? response.json() : response.text();
}

/**
 * Turns a failure body into one line. ASP.NET Core answers with three different shapes - a
 * ProblemDetails, Identity's { errors: { code: [...] } }, and this API's own { error } - and a view
 * should not have to know which one it got.
 *
 * A record write adds a fourth thing rather than a fourth shape: `{ error, rejected: [...] }`, whose
 * sentence is the `error` this already reads. The per-field reasons stay in `details` untouched, because
 * a form binds each of them to the box it is about rather than showing them as one line.
 */
function describe(status, body)
{
   if (typeof body === 'string' && body.trim())
   {
      return body.trim();
   }

   if (body && typeof body === 'object')
   {
      if (typeof body.error === 'string')
      {
         return body.error;
      }

      if (body.errors && typeof body.errors === 'object')
      {
         // Two shapes arrive under this key and both have to read as sentences. Model validation answers
         // `{ field: ["The Name field is required."] }`, while a rejected password answers
         // `[{ code, description }]` - and flattening that one without picking the description out reads
         // "[object Object]", which is exactly the message an administrator needs and cannot use.
         const messages = Object.values(body.errors)
            .flat()
            .map(entry => (typeof entry === 'string' ? entry : entry?.description ?? entry?.error ?? ''))
            .filter(Boolean);

         if (messages.length > 0)
         {
            return messages.join(' ');
         }
      }

      if (typeof body.detail === 'string')
      {
         return body.detail;
      }

      if (typeof body.title === 'string')
      {
         return body.title;
      }
   }

   return `Request failed with status ${status}.`;
}

async function refreshTokens()
{
   const refreshToken = getSession()?.refreshToken;
   if (!refreshToken)
   {
      return false;
   }

   try
   {
      const response = await fetch(url('/api/v1/auth/refresh'), {
         method: 'POST',
         headers: { 'content-type': 'application/json' },
         body: JSON.stringify({ refreshToken })
      });

      if (!response.ok)
      {
         clearSession();
         return false;
      }

      setSession(await response.json());
      return true;
   }
   catch
   {
      // A network failure is not an authentication failure - keep the session and let the caller
      // surface the error, rather than signing the user out because the backend blinked.
      return false;
   }
}

function refreshOnce()
{
   refreshInFlight ??= refreshTokens().finally(() => { refreshInFlight = null; });
   return refreshInFlight;
}

/**
 * Issues one request, refreshing the bearer token when it has expired or is rejected.
 *
 * @param {string} path        Absolute path on the backend, e.g. '/api/v1/query'.
 * @param {object} [options]
 * @param {string} [options.method]  Defaults to GET, or POST when a body is present.
 * @param {*}      [options.body]    Serialised as JSON, unless `contentType` says otherwise.
 * @param {string} [options.contentType]  Sends `body` verbatim under this type instead of as JSON.
 *    Bulk import is the reason it exists: a CSV or an NDJSON file is text the caller already holds, and
 *    wrapping it in JSON to send it would mean parsing it in the browser first.
 * @param {string} [options.accept]  What the caller wants back. The response is decoded by its own
 *    content type regardless, so a CSV export arrives as a string with nothing here having to know.
 * @param {boolean}[options.auth]    Send the bearer token. Defaults to true.
 * @param {AbortSignal} [options.signal]
 */
export async function request(path, { method, body, contentType, accept, auth = true, signal } = {})
{
   if (auth && getSession() && isExpiring())
   {
      await refreshOnce();
   }

   const send = () =>
   {
      const headers = {};
      if (body !== undefined)
      {
         headers['content-type'] = contentType ?? 'application/json';
      }

      if (accept)
      {
         headers.accept = accept;
      }

      const token = auth ? getSession()?.accessToken : null;
      if (token)
      {
         headers.authorization = `Bearer ${token}`;
      }

      return fetch(url(path), {
         method: method ?? (body === undefined ? 'GET' : 'POST'),
         headers,
         body: body === undefined ? undefined : (contentType ? body : JSON.stringify(body)),
         signal
      });
   };

   let response = await send();

   // A 401 on a token we believed to be valid means the server disagrees - the process restarted, or
   // the user was removed. One refresh, one retry, then give up.
   if (response.status === 401 && auth && getSession() && await refreshOnce())
   {
      response = await send();
   }

   const parsed = await parse(response);

   if (!response.ok)
   {
      if (response.status === 401 && auth)
      {
         clearSession();
      }

      throw new ApiError(response.status, describe(response.status, parsed), parsed);
   }

   return parsed;
}

/**
 * A query string from the parameters that were actually given.
 *
 * Every list-shaped endpoint here takes optional parameters and an omitted one means "the server
 * decides", which is not the same as an empty one: `?limit=` is a value the model binder has to reject,
 * and `?q=` narrows to rows containing the empty string. So absent stays absent, and a `Date` is written
 * as the instant the API reads.
 */
export function queryString(params)
{
   const search = new URLSearchParams();

   for (const [key, value] of Object.entries(params ?? {}))
   {
      if (value === null || value === undefined || value === '')
      {
         continue;
      }

      search.set(key, value instanceof Date ? value.toISOString() : String(value));
   }

   const text = search.toString();

   return text ? `?${text}` : '';
}
